import { isPlainObject as isPlainObjectPredicate } from 'es-toolkit/predicate';

/** Determine whether the candidate is a string literal. */
export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

/** Check for strings containing at least one non-whitespace character. */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/** Guard for arrays whose every element is a string. */
export function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

/** Narrow unknown values to plain object records (excluding arrays/null). */
export function isRecord<T extends Record<string, unknown> = Record<string, unknown>>(value: unknown): value is T {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Ensure the candidate is a POJO (object literal or plain prototype chain). */
export function isPlainObject<T extends Record<string, unknown> = Record<string, unknown>>(value: unknown): value is T {
  return isPlainObjectPredicate(value);
}

/** Trim strings and return null when the result is empty. */
export function toTrimmedNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Trim strings, returning undefined when empty to aid optional fields. */
export function toTrimmedNonEmptyStringOrUndefined(value: unknown): string | undefined {
  const trimmed = toTrimmedNonEmptyString(value);
  return trimmed ?? undefined;
}

/** Variant of toTrimmedNonEmptyString that preserves null for empty results. */
export function toTrimmedNonEmptyStringOrNull(value: unknown): string | null {
  return toTrimmedNonEmptyString(value);
}

/** Return the first non-empty string from a value or string array. */
export function firstPresentString(candidate: unknown): string | null {
  if (typeof candidate === 'string') {
    return toTrimmedNonEmptyString(candidate);
  }
  if (Array.isArray(candidate)) {
    for (const entry of candidate) {
      const trimmed = toTrimmedNonEmptyString(entry);
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return null;
}

/** Guard for finite numeric values. */
export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Guard for boolean literals. */
export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

/** Guard for plain objects or callable function values. */
export function isRecordLike<
  T extends Record<string, unknown> | ((...arguments_: never[]) => unknown) = Record<string, unknown>,
>(value: unknown): value is T {
  return isRecord(value) || typeof value === 'function';
}

/** Narrow values that are arrays while preserving readonly typing. */
export function asArray<T = unknown>(value: unknown): readonly T[] | undefined {
  return Array.isArray(value) ? (value as readonly T[]) : undefined;
}

/** Map a candidate into a trimmed string array, dropping empty entries. */
export function toNonEmptyStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return;
  }
  const entries = value
    .map((entry) => toTrimmedNonEmptyString(entry))
    .filter((entry): entry is string => entry !== null);
  return entries.length > 0 ? entries : undefined;
}

/** Normalize @handles to plain username strings. */
export function normalizeHandle(value: unknown): string | null {
  const trimmed = toTrimmedNonEmptyString(value);
  if (!trimmed) {
    return null;
  }
  return trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
}

/** Type-safe property existence guard for object records. */
export function hasProperty<T extends Record<string, unknown>, K extends PropertyKey>(
  value: T,
  key: K
): value is T & Record<K, unknown> {
  return key in value;
}

/** Exhaustiveness check helper for switch/if-else chains. */
export function assertUnreachable(value: never, message?: string): never {
  throw new Error(message ?? `Unexpected value: ${String(value)}`);
}

/** Guard for Node.js ErrnoException instances. */
export function isErrnoException(value: unknown): value is NodeJS.ErrnoException {
  return isRecord(value) && 'code' in value;
}

/** Safely read a string field from an unknown record. */
export function readStringField(data: unknown, key: string): string | undefined {
  if (!isRecord(data)) {
    return;
  }
  const value = data[key];
  return typeof value === 'string' ? value : undefined;
}

/** Safely read a number field from an unknown record. */
export function readNumberField(data: unknown, key: string): number | undefined {
  if (!isRecord(data)) {
    return;
  }
  const value = data[key];
  return typeof value === 'number' ? value : undefined;
}

/** Safely read a boolean field from an unknown record. */
export function readBooleanField(data: unknown, key: string): boolean | undefined {
  if (!isRecord(data)) {
    return;
  }
  const value = data[key];
  return typeof value === 'boolean' ? value : undefined;
}

/** Safely read a property from an unknown record without forcing a cast. */
export function getRecordValue<T = unknown>(record: Record<string, T> | null | undefined, key: string): T | undefined {
  if (!record) {
    return;
  }
  return record[key];
}

/** Extract error message from unknown value (Error instance or string). */
export function toErrorMessage(value: unknown): string | null {
  if (value instanceof Error) {
    return value.message;
  }
  if (typeof value === 'string') {
    return value;
  }
  return null;
}

/** Guard for Promise-like values (thenable objects). */
export function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  if (!isRecord(value)) {
    return false;
  }
  // biome-ignore lint/complexity/useLiteralKeys: TypeScript requires bracket access for index signatures.
  const then = value['then'];
  return typeof then === 'function';
}

/**
 * Factory that creates a Set-based type guard for string literal unions.
 * Provides O(1) lookup performance vs O(n) for array-based guards.
 *
 * @example
 * const MODES = ['read', 'write', 'admin'] as const;
 * type Mode = typeof MODES[number];
 * const isMode = createSetBasedGuard<Mode>(MODES);
 * isMode('read') // true
 * isMode('invalid') // false
 */
export function createSetBasedGuard<T extends string>(values: readonly T[]): (value: unknown) => value is T {
  const valueSet = new Set<string>(values);
  return (value: unknown): value is T => typeof value === 'string' && valueSet.has(value);
}

/**
 * Extract string value from unknown, returning undefined if not a string.
 * Useful for safe property reads from untyped objects.
 *
 * @example
 * const name = readString(user.name);  // string | undefined
 */
export function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * Extract finite number from unknown, returning undefined if not a valid number.
 * Useful for safe property reads from untyped objects.
 *
 * @example
 * const count = readNumber(obj.count);  // number | undefined
 */
export function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
